/**
 * Sprite Context Menu
 * Right-click/long-press menu for sprite images
 */

import { showNotification, parseSpriteUrl, confirmRedirectToCreateVideo, resolveTranslation } from './services/spriteUpgradeService.js';
import { persistSpriteVote, SPRITE_DOWNVOTE_REASONS } from './services/spriteVoteService.js';
import { removeSpriteFromPreferences } from './services/spritePreferences.js';
import { applyTranslationsToContainer, getCurrentLanguage, loadTranslations } from '../../translations.js';
import { getTranscriptForLayout } from '../shared/history-resolution.js';

let activeMenu = null;
let menuHideTimer = null;
let hangoutStateModulePromise = null;
let contextMenuOpenGeneration = 0;

const LONG_PRESS_DURATION_MS = 500;
const LONG_PRESS_MOVE_THRESHOLD_PX = 10;

const HANGOUT_DOWNVOTE_SOURCES = new Set(['character-image', 'chatbot-message-avatar']);

const loadHangoutState = () => {
    if (!hangoutStateModulePromise) {
        hangoutStateModulePromise = import('../state/HangoutState.js');
    }
    return hangoutStateModulePromise;
};

const getHangoutStateInstance = async () => {
    const { HangoutState } = await loadHangoutState();
    return HangoutState.getInstance();
};

const shouldSkipHangoutLookup = (override) => {
    if (!override || typeof override !== 'object') return false;
    const hasIds = override.characterId != null && String(override.characterId) !== ''
        && override.actionId != null && override.actionId !== '';
    return hasIds && Object.prototype.hasOwnProperty.call(override, 'path');
};

const cancelMenuHideTimer = () => {
    if (!menuHideTimer) return;
    clearTimeout(menuHideTimer);
    menuHideTimer = null;
};

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const formatActionNameForDisplay = (name) => {
    const resolvedName = typeof name === 'string' ? name.trim() : '';
    if (!resolvedName) return '';
    return resolvedName.replace(/_/g, ' ');
};

const resolveAssistantPathForSprite = async (characterId, actionId) => {
    if (!characterId) return null;
    try {
        const hangoutState = await getHangoutStateInstance();
        const visible = getTranscriptForLayout(hangoutState);
        const assistants = visible.filter((message) =>
            message?.role === 'assistant'
            && String(message?.characterId ?? '') === String(characterId)
            && typeof message?.path === 'string'
            && message.path.trim()
        );
        if (assistants.length === 0) return null;
        const actionKey = actionId != null && actionId !== undefined ? String(actionId) : '';
        const actionMatch = actionKey
            ? [...assistants].reverse().find((message) => {
                const messageAction = String(message.actionId ?? message.action_id ?? message.actionName ?? '');
                return messageAction === actionKey;
            })
            : null;
        return (actionMatch || assistants[assistants.length - 1])?.path || null;
    } catch (error) {
        console.warn('[SpriteContextMenu] Could not resolve assistant path:', error);
        return null;
    }
};

const getMediaUrl = (img) => img?.currentSrc || img?.src || '';

const getSpriteDataFromMedia = (container, img) => {
    const spriteUrl = getMediaUrl(img);
    const parsed = parseSpriteUrl(spriteUrl);
    return {
        characterId: container?.getAttribute('data-character-id') || parsed.characterId,
        outfitId: parsed.outfitId,
        actionId: parsed.actionId,
        videoActionId: parsed.videoActionId,
        spriteUrl,
        videoUrl: spriteUrl,
        characterGender: '',
        actionName: '',
        path: null,
        source: null
    };
};

/**
 * Get sprite data from container and image element
 * @param {HTMLElement} container - Character container element
 * @param {HTMLImageElement} img - Sprite image element
 * @returns {Promise<{characterId: string|null, outfitId: string|null, actionId: string|null, videoActionId: string|null, spriteUrl: string, characterGender: string, actionName: string, path: string|null}>}
 */
const getSpriteData = async (container, img) => {
    const spriteUrl = getMediaUrl(img);

    let characterId = container?.getAttribute('data-character-id');
    let outfitId = null;
    let actionId = null;
    let videoActionId = null;
    let characterGender = '';
    let actionName = '';

    if (characterId) {
        try {
            const hangoutState = await getHangoutStateInstance();
            const session = hangoutState.getCurrentSession();
            const character = session?.allCharacters?.find(c => String(c.id) === String(characterId));

            if (character) {
                outfitId = character.outfitId || '0';
                actionId = character.actionId ?? character.spriteState?.actionId ?? null;
                characterGender = character.gender || '';
                actionName = character.actionName || character.spriteState?.action || '';
                if (actionId === null || actionId === undefined || Number.isNaN(Number(actionId))) {
                    const spriteStateAction = character.spriteState?.action;
                    if (spriteStateAction != null && /^\d+$/.test(String(spriteStateAction))) {
                        actionId = spriteStateAction;
                    }
                }
            }
        } catch (e) {
            console.warn('[SpriteContextMenu] Could not get character data from HangoutState:', e);
        }
    }

    const parsed = parseSpriteUrl(spriteUrl);
    if (!characterId && parsed.characterId) characterId = parsed.characterId;
    if ((outfitId === null || outfitId === undefined) && parsed.outfitId) outfitId = parsed.outfitId;
    const hasNumericActionId = actionId != null && actionId !== undefined && Number.isInteger(Number(actionId));
    if (!hasNumericActionId && parsed.actionId) actionId = parsed.actionId;
    if (parsed.videoActionId) videoActionId = parsed.videoActionId;

    if (!String(actionName || '').trim() && characterId) {
        try {
            const hangoutState = await getHangoutStateInstance();
            const visible = getTranscriptForLayout(hangoutState);
            const actionKey = actionId != null && actionId !== undefined ? String(actionId) : '';
            const match = [...visible].reverse().find((message) => {
                if (message?.role !== 'assistant') return false;
                if (String(message?.characterId ?? message?.character_id ?? '') !== String(characterId)) return false;
                const messageActionName = typeof message.actionName === 'string' ? message.actionName.trim() : '';
                if (!messageActionName) return false;
                if (!actionKey) return true;
                return String(message.actionId ?? message.action_id ?? '') === actionKey;
            });
            if (match?.actionName) actionName = match.actionName;
        } catch (error) {
            console.warn('[SpriteContextMenu] Could not resolve action name from messages:', error);
        }
    }

    const path = await resolveAssistantPathForSprite(characterId, actionId);
    return {
        characterId,
        outfitId,
        actionId,
        videoActionId,
        spriteUrl,
        videoUrl: spriteUrl,
        characterGender,
        actionName,
        path,
        source: null
    };
};

const buildActionNameHtml = (actionName) => {
    const displayName = formatActionNameForDisplay(actionName);
    if (!displayName) return '';
    return `<div class="sprite-context-menu-action-name">${escapeHtml(displayName)}</div>`;
};

const isOnCreateVideoPage = () => window.location.pathname.includes('create-video');

const buildVoteItemsHtml = (isVideo) => `
        <button class="sprite-context-menu-item" data-action="upvote" role="menuitem" tabindex="0">
            <i class="fas fa-thumbs-up"></i>
            <span data-translate="${isVideo ? 'sprite_context.upvote_video' : 'sprite_context.upvote_image'}"></span>
        </button>
        <button class="sprite-context-menu-item" data-action="downvote" role="menuitem" tabindex="0">
            <i class="fas fa-thumbs-down"></i>
            <span data-translate="${isVideo ? 'sprite_context.downvote_video' : 'sprite_context.downvote_image'}"></span>
        </button>
`;

const buildUpgradeItemHtml = (isVideo) => {
    if (isOnCreateVideoPage()) return '';
    if (isVideo) {
        return `
        <button class="sprite-context-menu-item" data-action="upgrade_video" role="menuitem" tabindex="0">
            <i class="fas fa-wand-magic-sparkles"></i>
            <span data-translate="sprite_context.upgrade_video_quality"></span>
        </button>`;
    }
    return `
        <button class="sprite-context-menu-item" data-action="upgrade" role="menuitem" tabindex="0">
            <i class="fas fa-video"></i>
            <span data-translate="sprite_context.upgrade_to_video"></span>
        </button>`;
};

const buildMenuInnerHtml = (isVideo, redoItemHtml, actionName, createImageItemHtml = '') => (
    `${buildActionNameHtml(actionName)}
        ${createImageItemHtml}
        ${buildUpgradeItemHtml(isVideo)}
        ${buildVoteItemsHtml(isVideo)}
        ${redoItemHtml}`
);

const buildDownvoteReasonsHtml = (actionName) => `
    ${buildActionNameHtml(actionName)}
    <div class="sprite-context-menu-reason-label" data-translate="sprite_context.downvote_reason_prompt"></div>
    ${SPRITE_DOWNVOTE_REASONS.map((reason) => `
        <button class="sprite-context-menu-item" data-action="downvote_reason" data-reason="${reason}" role="menuitem" tabindex="0">
            <span data-translate="sprite_context.downvote_reasons.${reason}"></span>
        </button>
    `).join('')}
`;

const positionMenu = (menu, x, y) => {
    const menuRect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let finalX = x;
    let finalY = y;

    if (x + menuRect.width > viewportWidth - 10) {
        finalX = viewportWidth - menuRect.width - 10;
    }
    if (y + menuRect.height > viewportHeight - 10) {
        finalY = y - menuRect.height - 10;
    }
    if (finalX < 10) finalX = 10;
    if (finalY < 10) finalY = 10;

    menu.style.left = `${finalX}px`;
    menu.style.top = `${finalY}px`;
};

const applyMenuTranslations = async (menu, isVideo) => {
    const lang = getCurrentLanguage();
    await loadTranslations('hangout', lang);
    menu.setAttribute('data-translate-attr', isVideo
        ? 'aria-label:sprite_context.menu_aria_video'
        : 'aria-label:sprite_context.menu_aria_sprite');
    menu.setAttribute('aria-label', isVideo ? 'Video options' : 'Sprite options');
    applyTranslationsToContainer(menu, 'hangout', lang);
};

const handleHangoutDownvoteSideEffects = async (spriteData) => {
    if (!HANGOUT_DOWNVOTE_SOURCES.has(spriteData.source)) return;

    try {
        await removeSpriteFromPreferences({
            characterId: spriteData.characterId,
            outfitId: spriteData.outfitId,
            actionId: spriteData.actionId
        });
    } catch (error) {
        console.warn('[SpriteContextMenu] Failed to remove sprite from preferences:', error);
    }

    if (!spriteData.path) return;
    const { redoAssistantActionDecision } = await import('./message-utils.js');
    await redoAssistantActionDecision({
        path: spriteData.path,
        characterId: spriteData.characterId,
        outfitId: spriteData.outfitId,
        actionId: spriteData.actionId
    });
};

const submitVote = async (spriteData, isVideo, vote, reason = null) => {
    if (!spriteData.characterId || spriteData.actionId === null || spriteData.actionId === undefined) {
        await showNotification('Could not determine sprite details. Please try again.', 'error');
        return false;
    }
    if (isVideo && (spriteData.videoActionId === null || spriteData.videoActionId === undefined || spriteData.videoActionId === '')) {
        await showNotification('Could not determine video details. Please try again.', 'error');
        return false;
    }

    const result = await persistSpriteVote({
        isVideo,
        characterId: spriteData.characterId,
        outfitId: spriteData.outfitId ?? 0,
        actionId: spriteData.actionId,
        videoActionId: spriteData.videoActionId,
        vote,
        reason
    });
    if (!result?.success) {
        if (result?.error_code === 'AUTH_REQUIRED') {
            const { showFreeAccountModal } = await import('../../components/FreeAccountModal.js');
            await showFreeAccountModal('sprite-vote');
            return false;
        }
        await showNotification(result?.error || 'Could not save your vote.', 'error');
        return false;
    }

    const successKey = vote === 1
        ? (isVideo ? 'sprite_context.vote_success_upvote_video' : 'sprite_context.vote_success_upvote_image')
        : (isVideo ? 'sprite_context.vote_success_downvote_video' : 'sprite_context.vote_success_downvote_image');
    const successFallback = vote === 1
        ? (isVideo ? 'Video upvoted' : 'Image upvoted')
        : (isVideo ? 'Video downvoted' : 'Image downvoted');
    await showNotification(resolveTranslation(successKey, successFallback), 'success', { duration: 2000 });

    if (vote === -1) {
        await handleHangoutDownvoteSideEffects(spriteData);
    }
    return true;
};

const isStaleMenuOpen = (generation, menu) =>
    generation !== contextMenuOpenGeneration || activeMenu !== menu;

const renderContextMenu = async (x, y, img, spriteData, overrideSpriteData, generation) => {
    cancelMenuHideTimer();
    if (activeMenu) {
        activeMenu._cleanup?.();
        activeMenu.classList.remove('show');
    }

    if (!spriteData.videoActionId) {
        const parsed = parseSpriteUrl(spriteData.spriteUrl || getMediaUrl(img));
        if (parsed.videoActionId) spriteData.videoActionId = parsed.videoActionId;
    }
    const isVideo = overrideSpriteData?.isVideo === true || isVideoElement(img);
    const redoItemHtml = spriteData.path
        ? `
        <button class="sprite-context-menu-item" data-action="redo_decision" role="menuitem" tabindex="0">
            <i class="fas fa-arrows-rotate"></i>
            <span data-translate="sprite_context.redo_action_decision"></span>
        </button>
    `
        : '';
    const createImageItemHtml = spriteData.source === 'chatbot-message-avatar' && spriteData.path
        ? `
        <button class="sprite-context-menu-item" data-action="create_image" role="menuitem" tabindex="0">
            <i class="fas fa-image"></i>
            <span data-translate="sprite_context.create_image_for_message">Create image for this message</span>
        </button>
    `
        : '';

    let menu = activeMenu;
    if (!menu) {
        menu = document.createElement('div');
        menu.className = 'sprite-context-menu';
        menu.setAttribute('role', 'menu');
        document.body.appendChild(menu);
        activeMenu = menu;
    }

    document.querySelectorAll('.sprite-context-menu').forEach((el) => {
        if (el !== menu) el.remove();
    });

    menu.innerHTML = buildMenuInnerHtml(isVideo, redoItemHtml, spriteData.actionName, createImageItemHtml);
    await applyMenuTranslations(menu, isVideo);
    if (isStaleMenuOpen(generation, menu)) return;

    if (!menu.isConnected) document.body.appendChild(menu);

    positionMenu(menu, x, y);

    requestAnimationFrame(() => {
        if (activeMenu === menu) menu.classList.add('show');
    });

    const showReasonPicker = async () => {
        menu.innerHTML = buildDownvoteReasonsHtml(spriteData.actionName);
        await applyMenuTranslations(menu, isVideo);
        if (isStaleMenuOpen(generation, menu)) return;
        positionMenu(menu, x, y);
        const firstItem = /** @type {HTMLElement} */ (menu.querySelector('.sprite-context-menu-item'));
        firstItem?.focus();
    };

    const handleItemClick = async (e) => {
        const item = e.target.closest('.sprite-context-menu-item');
        if (!item) return;

        const action = item.getAttribute('data-action');
        if (action === 'downvote') {
            await showReasonPicker();
            return;
        }

        hideContextMenu();

        if (action === 'upgrade') {
            await confirmRedirectToCreateVideo(spriteData);
        } else if (action === 'upgrade_video') {
            await confirmRedirectToCreateVideo(spriteData, 'upgrade');
        } else if (action === 'upvote') {
            await submitVote(spriteData, isVideo, 1);
        } else if (action === 'downvote_reason') {
            const reason = item.getAttribute('data-reason');
            await submitVote(spriteData, isVideo, -1, reason);
        } else if (action === 'create_image') {
            const { openMessageImagePromptModal } = await import('./message-image-prompt-modal.js');
            await openMessageImagePromptModal(spriteData);
        } else if (action === 'redo_decision') {
            const { showConfirm } = await import('../../utils/UnifiedNotificationSystem.js');
            const confirmed = await showConfirm(
                'Are you sure you want to redo the action decision for this message?',
                { yesText: 'Confirm', noText: 'Cancel', type: 'warning' }
            );
            if (!confirmed) return;
            const { redoAssistantActionDecision } = await import('./message-utils.js');
            await redoAssistantActionDecision({
                path: spriteData.path,
                characterId: spriteData.characterId,
                outfitId: spriteData.outfitId,
                actionId: spriteData.actionId
            });
        }
    };

    const handleKeydown = (e) => {
        if (e.key === 'Escape') {
            hideContextMenu();
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const items = menu.querySelectorAll('.sprite-context-menu-item');
            const currentIndex = Array.from(items).indexOf(document.activeElement);
            let nextIndex;

            if (e.key === 'ArrowDown') {
                nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
            } else {
                nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
            }

            /** @type {HTMLElement} */ (items[nextIndex])?.focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            document.activeElement?.click();
        }
    };

    const handleClickOutside = (e) => {
        if (!menu.contains(e.target)) {
            hideContextMenu();
        }
    };

    const handleTouchStartOutside = (e) => {
        if (e.touches && e.touches.length > 1) return;

        const touch = e.touches?.[0];
        if (!touch) return;

        const target = document.elementFromPoint(touch.clientX, touch.clientY);
        if (!target) return;

        if (!menu.contains(target)) {
            hideContextMenu();
        }
    };

    menu.addEventListener('click', handleItemClick);
    menu.addEventListener('keydown', handleKeydown);

    const outsideBindTimer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside, true);
        document.addEventListener('contextmenu', handleClickOutside, true);
        document.addEventListener('touchstart', handleTouchStartOutside, true);
    }, 50);

    menu._cleanup = () => {
        clearTimeout(outsideBindTimer);
        menu.removeEventListener('click', handleItemClick);
        menu.removeEventListener('keydown', handleKeydown);
        document.removeEventListener('click', handleClickOutside, true);
        document.removeEventListener('contextmenu', handleClickOutside, true);
        document.removeEventListener('touchstart', handleTouchStartOutside, true);
    };

    const firstItem = /** @type {HTMLElement} */ (menu.querySelector('.sprite-context-menu-item'));
    firstItem?.focus();
};

/**
 * Create and show the context menu
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {HTMLElement} container - Character container
 * @param {HTMLImageElement|HTMLVideoElement} img - Sprite image or video element
 */
export const showContextMenu = (x, y, container, img, overrideSpriteData = null) => {
    const override = overrideSpriteData && typeof overrideSpriteData === 'object' ? overrideSpriteData : null;
    const generation = ++contextMenuOpenGeneration;

    const open = async () => {
        const base = shouldSkipHangoutLookup(override)
            ? getSpriteDataFromMedia(container, img)
            : await getSpriteData(container, img);
        if (generation !== contextMenuOpenGeneration) return;
        const spriteData = { ...base, ...override };
        await renderContextMenu(x, y, img, spriteData, override, generation);
    };

    void open();
};

/**
 * Attach right-click and long-press listeners that open the sprite context menu.
 * @param {HTMLElement} targetEl
 * @param {{
 *   container?: HTMLElement,
 *   selector?: string,
 *   getMediaEl?: (eventTarget: EventTarget|null) => HTMLElement|null,
 *   getOverrideSpriteData?: (mediaEl: HTMLElement) => object|false|null,
 *   overrideSpriteData?: object|null
 * }} [options]
 */
export const attachSpriteContextMenuListeners = (targetEl, options = {}) => {
    if (!targetEl || targetEl._contextMenuAttached) return;
    targetEl._contextMenuAttached = true;

    let longPressTimer = null;
    let touchStartX = 0;
    let touchStartY = 0;

    const resolveMediaEl = (eventTarget) => {
        if (typeof options.selector === 'string') {
            const match = eventTarget?.closest?.(options.selector);
            if (!match || !targetEl.contains(match)) return null;
            return match;
        }
        if (typeof options.getMediaEl === 'function') {
            return options.getMediaEl(eventTarget);
        }
        return targetEl;
    };

    const openMenuAt = (x, y, eventTarget) => {
        const mediaEl = resolveMediaEl(eventTarget);
        if (!mediaEl) return;
        if (!isImageElement(mediaEl) && !isVideoElement(mediaEl)) return;
        const override = typeof options.getOverrideSpriteData === 'function'
            ? options.getOverrideSpriteData(mediaEl)
            : (options.overrideSpriteData ?? null);
        if (override === false) return;
        const container = options.container || mediaEl;
        showContextMenu(x, y, container, mediaEl, override);
    };

    const handleContextMenu = (event) => {
        if (!resolveMediaEl(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        openMenuAt(event.clientX, event.clientY, event.target);
    };

    const handleTouchStart = (event) => {
        if (event.touches.length !== 1) return;
        if (!resolveMediaEl(event.target)) return;
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        longPressTimer = setTimeout(() => {
            openMenuAt(touchStartX, touchStartY, event.target);
        }, LONG_PRESS_DURATION_MS);
    };

    const handleTouchMove = (event) => {
        if (!longPressTimer) return;
        const touch = event.touches[0];
        const dx = Math.abs(touch.clientX - touchStartX);
        const dy = Math.abs(touch.clientY - touchStartY);
        if (dx > LONG_PRESS_MOVE_THRESHOLD_PX || dy > LONG_PRESS_MOVE_THRESHOLD_PX) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
        }
    };

    const handleTouchEnd = () => {
        if (!longPressTimer) return;
        clearTimeout(longPressTimer);
        longPressTimer = null;
    };

    targetEl.addEventListener('contextmenu', handleContextMenu);
    targetEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    targetEl.addEventListener('touchmove', handleTouchMove, { passive: true });
    targetEl.addEventListener('touchend', handleTouchEnd);
    targetEl.addEventListener('touchcancel', handleTouchEnd);

    targetEl._cleanupContextMenu = () => {
        targetEl.removeEventListener('contextmenu', handleContextMenu);
        targetEl.removeEventListener('touchstart', handleTouchStart);
        targetEl.removeEventListener('touchmove', handleTouchMove);
        targetEl.removeEventListener('touchend', handleTouchEnd);
        targetEl.removeEventListener('touchcancel', handleTouchEnd);
        if (longPressTimer) clearTimeout(longPressTimer);
        targetEl._contextMenuAttached = false;
    };
};

/**
 * Hide the context menu
 */
export const hideContextMenu = () => {
    if (!activeMenu) return;

    cancelMenuHideTimer();
    activeMenu._cleanup?.();
    activeMenu.classList.remove('show');

    const menuToRemove = activeMenu;
    menuHideTimer = setTimeout(() => {
        menuHideTimer = null;
        if (activeMenu !== menuToRemove) return;
        menuToRemove.remove();
        activeMenu = null;
    }, 150);
};

/**
 * Check if element is an image (not video)
 * @param {HTMLElement} element
 * @returns {boolean}
 */
export const isImageElement = (element) => {
    return element?.tagName === 'IMG';
};

/**
 * Check if element is a video
 * @param {HTMLElement} element
 * @returns {boolean}
 */
export const isVideoElement = (element) => {
    return element?.tagName === 'VIDEO';
};
